import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingDown, 
  DollarSign, 
  Target, 
  Calendar,
  Award,
  Sparkles,
  PiggyBank,
  BarChart3
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReductionCounterProps {
  habitName: string;
  habitIcon: React.ReactNode;
  currentAmount: number;
  previousAmount?: number;
  unit: string;
  costPerUnit?: number;
  currency?: string;
  weeklyGoal?: number;
  monthlyGoal?: number;
  onCostUpdate?: (cost: number) => void;
}

export default function ReductionCounter({
  habitName,
  habitIcon,
  currentAmount,
  previousAmount = 0,
  unit,
  costPerUnit = 0,
  currency = 'USD',
  weeklyGoal = 0,
  monthlyGoal = 0,
  onCostUpdate
}: ReductionCounterProps) {
  const [dailyReduction, setDailyReduction] = useState(0);
  const [weeklyReduction, setWeeklyReduction] = useState(0);
  const [monthlyReduction, setMonthlyReduction] = useState(0);
  const [showSavings, setShowSavings] = useState(false);
  const [customCost, setCustomCost] = useState(costPerUnit);
  const [animateReduction, setAnimateReduction] = useState(false);
  const { toast } = useToast();

  // Mock data for demonstration - in real app this would come from database
  const weeklyData = [15, 12, 10, 8, 6, 5, currentAmount]; // Last 7 days
  const monthlyData = Array.from({ length: 30 }, (_, i) => 
    Math.max(0, 20 - (i * 0.5) + Math.random() * 3)
  ); // Last 30 days with downward trend

  useEffect(() => {
    // Calculate reductions
    const dailyDiff = Math.max(0, previousAmount - currentAmount);
    const weeklyTotal = weeklyData.reduce((sum, day) => sum + day, 0);
    const expectedWeekly = previousAmount * 7;
    const weeklyDiff = Math.max(0, expectedWeekly - weeklyTotal);
    
    const monthlyTotal = monthlyData.reduce((sum, day) => sum + day, 0);
    const expectedMonthly = previousAmount * 30;
    const monthlyDiff = Math.max(0, expectedMonthly - monthlyTotal);

    setDailyReduction(dailyDiff);
    setWeeklyReduction(weeklyDiff);
    setMonthlyReduction(monthlyDiff);

    // Animate when there's a reduction
    if (dailyDiff > 0) {
      setAnimateReduction(true);
      setTimeout(() => setAnimateReduction(false), 2000);
    }
  }, [currentAmount, previousAmount]);

  const calculateSavings = (reduction: number) => {
    return (reduction * customCost).toFixed(2);
  };

  const getProgressToGoal = (current: number, goal: number) => {
    if (goal === 0) return 0;
    return Math.min((goal - current) / goal * 100, 100);
  };

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;

  return (
    <div className="space-y-4">
      {/* Main Reduction Display */}
      <Card className={`p-6 transition-all duration-500 ${
        dailyReduction > 0 ? 'animate-success-glow border-accent' : 'border-border'
      }`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-500 ${
            dailyReduction > 0 ? 'bg-accent animate-bounce-celebration' : 'bg-muted'
          }`}>
            {typeof habitIcon === 'string' ? (
              <div className="text-2xl">{habitIcon}</div>
            ) : (
              <div className={`transition-colors duration-500 ${
                dailyReduction > 0 ? 'text-accent-foreground' : 'text-muted-foreground'
              }`}>
                {habitIcon}
              </div>
            )}
          </div>
          
          <h3 className="text-xl font-bold mb-2 text-foreground">{habitName} Reduction</h3>
          
          {dailyReduction > 0 ? (
            <div className="space-y-2">
              <div className="text-2xl font-bold text-accent flex items-center justify-center gap-2">
                <TrendingDown className="w-6 h-6" />
                -{dailyReduction} {unit} today
              </div>
              <Badge className="bg-accent text-accent-foreground animate-pulse">
                Great progress!
              </Badge>
            </div>
          ) : (
            <div className="text-lg text-muted-foreground">
              No reduction today - that's okay, keep trying!
            </div>
          )}
        </div>

        {/* Cost Settings */}
        <div className="mb-6 p-4 bg-muted/20 rounded-lg">
          <Label className="text-sm font-medium mb-2 block">
            Cost per {unit.slice(0, -1)} ({currency})
          </Label>
          <Input
            type="number"
            step="0.01"
            value={customCost}
            onChange={(e) => {
              const newCost = parseFloat(e.target.value) || 0;
              setCustomCost(newCost);
              onCostUpdate?.(newCost);
            }}
            className="text-center"
            placeholder="0.00"
          />
        </div>

        {/* Savings Display */}
        {customCost > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-accent/10 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <DollarSign className="w-4 h-4 text-accent" />
                <span className="text-xs font-medium text-accent">Today</span>
              </div>
              <div className={`text-lg font-bold text-accent ${
                dailyReduction > 0 ? 'animate-money-float' : ''
              }`}>
                {currencySymbol}{calculateSavings(dailyReduction)}
              </div>
            </div>
            
            <div className="text-center p-3 bg-primary/10 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-primary">Week</span>
              </div>
              <div className="text-lg font-bold text-primary">
                {currencySymbol}{calculateSavings(weeklyReduction)}
              </div>
            </div>
            
            <div className="text-center p-3 bg-primary/10 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <PiggyBank className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-primary">Month</span>
              </div>
              <div className="text-lg font-bold text-primary">
                {currencySymbol}{calculateSavings(monthlyReduction)}
              </div>
            </div>
          </div>
        )}

        {/* Progress to Goals */}
        {weeklyGoal > 0 && (
          <div className="space-y-3 mb-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  Weekly Goal Progress
                </span>
                <span>{currentAmount} / {weeklyGoal} {unit}</span>
              </div>
              <Progress 
                value={getProgressToGoal(currentAmount * 7, weeklyGoal * 7)} 
                className="h-2"
              />
            </div>
          </div>
        )}

        {/* Achievements */}
        <div className="flex flex-wrap gap-2 justify-center">
          {dailyReduction > 0 && (
            <Badge variant="outline" className="animate-sparkle">
              <Sparkles className="w-3 h-3 mr-1" />
              Daily Reducer
            </Badge>
          )}
          {weeklyReduction > previousAmount * 2 && (
            <Badge variant="outline" className="bg-primary/10">
              <Award className="w-3 h-3 mr-1" />
              Weekly Champion
            </Badge>
          )}
          {monthlyReduction > previousAmount * 10 && (
            <Badge variant="outline" className="bg-accent/10">
              <TrendingDown className="w-3 h-3 mr-1" />
              Transformation Master
            </Badge>
          )}
        </div>
      </Card>

      {/* Trend Visualisation */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            7-Day Trend
          </h4>
          <Badge variant="outline" className="text-xs">
            {weeklyReduction > 0 ? 'Reducing' : 'Stable'}
          </Badge>
        </div>
        
        {/* Simple bar chart */}
        <div className="flex items-end justify-between h-20 gap-1">
          {weeklyData.map((value, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div 
                className={`w-full transition-all duration-500 rounded-t ${
                  index === weeklyData.length - 1 ? 'bg-accent' : 'bg-muted'
                } ${animateReduction && index === weeklyData.length - 1 ? 'animate-unit-decrease' : ''}`}
                style={{ 
                  height: `${Math.max(5, (value / Math.max(...weeklyData)) * 100)}%`,
                }}
              />
              <div className="text-xs text-muted-foreground mt-1">
                {index === weeklyData.length - 1 ? 'Today' : `${weeklyData.length - 1 - index}d`}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}